// Registers the PWA service worker + shows a lightweight install hint on iOS
// (Android/Chrome fires beforeinstallprompt natively, so we hook that too).
//
// Called once from index.js after React mounts.

const IOS_HINT_KEY = 'iosInstallHintDismissed';

export function registerPWA() {
  if (!('serviceWorker' in navigator)) return;

  // Register after `load` so we don't compete with the first render.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch((err) => {
      // Registration failure is not user-visible — just log for debugging.
      console.warn('[PWA] service worker registration failed:', err);
    });
  });

  wireAndroidInstallPrompt();
  wireIosInstallHint();
}

// Android/Chrome: `beforeinstallprompt` is fired by the browser when the site
// meets PWA install criteria (manifest + SW + HTTPS). We stash the event so a
// button in the app can trigger the install prompt later.
let deferredInstallPrompt = null;
function wireAndroidInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    // Signal the app that we have an installable state.
    window.dispatchEvent(new CustomEvent('pwa:installable'));
  });
}

export function promptInstall() {
  if (!deferredInstallPrompt) return Promise.resolve('unavailable');
  deferredInstallPrompt.prompt();
  return deferredInstallPrompt.userChoice.then((c) => {
    deferredInstallPrompt = null;
    return c.outcome; // 'accepted' | 'dismissed'
  });
}

// iOS Safari never fires beforeinstallprompt — installation is a manual gesture
// (Share sheet → 'Add to Home Screen'). Show a one-time hint the first time an
// iOS user opens the site in Safari (NOT when already running standalone).
function wireIosInstallHint() {
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const alreadyStandalone = window.navigator.standalone === true;
  if (!isIos || !isSafari || alreadyStandalone) return;
  if (localStorage.getItem(IOS_HINT_KEY) === '1') return;

  // Wait a beat so the app renders first; then inject a small toast.
  setTimeout(() => showIosHint(), 2500);
}

function showIosHint() {
  const el = document.createElement('div');
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', 'Install this app');
  el.style.cssText = [
    'position:fixed', 'left:16px', 'right:16px', 'bottom:16px', 'z-index:2147483647',
    'background:#ffffff', 'border:1px solid #e5e7eb', 'border-radius:14px',
    'box-shadow:0 12px 32px rgba(0,0,0,0.16)', 'padding:14px 16px',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'font-size:14px', 'color:#1f2937', 'max-width:520px', 'margin:0 auto',
    'display:flex', 'align-items:flex-start', 'gap:12px',
  ].join(';');
  el.innerHTML = `
    <img src="/logo.png" alt="" style="width:36px;height:36px;object-fit:contain;flex:0 0 auto" />
    <div style="flex:1;line-height:1.4">
      <div style="font-weight:600;margin-bottom:2px">Install St. Michael on your iPhone</div>
      <div style="color:#6b7280;font-size:12.5px">
        Tap <span aria-label="Share button" style="display:inline-block;transform:translateY(2px)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        </span> then <b>Add to Home Screen</b>.
      </div>
    </div>
    <button aria-label="Dismiss" style="background:none;border:none;color:#9ca3af;font-size:20px;cursor:pointer;padding:0 4px;line-height:1">×</button>
  `;
  const dismiss = () => {
    try { el.remove(); } catch (_) {}
    localStorage.setItem(IOS_HINT_KEY, '1');
  };
  el.querySelector('button').addEventListener('click', dismiss);
  document.body.appendChild(el);
  // Auto-dismiss after 30s so it doesn't linger forever.
  setTimeout(dismiss, 30000);
}
